<template>
  <div id="submit-bid-tab" class="tab is-full-height">
    <div v-if="bidPackageCompanyFileStore.isLoading" class="submit-bid-loading">
      <LoadingIndicator message="Submitting bid…" />
    </div>

    <div v-if="isBidSubmissionClosed" class="notification is-warning is-light">
      {{ bidSubmissionClosedMessage }}
    </div>

    <template v-if="showInvites && !isBidSubmissionClosed">
      <div class="label">Bid Forms</div>

      <div class="box overflow-scroll">
        <div class="has-margin-bottom">Please click the below link{{ invites.length > 1 ? 's' : '' }} and fill out our Bid Form{{ invites.length > 1 ? 's' : '' }}</div>
        <ul>
          <li v-for="invite in invites">
            <a class="is-link" :href="invite.url" target="_blank">{{ invite.name }}</a>
          </li>
        </ul>
      </div>
    </template>

    <template v-if="showUpload && !isBidSubmissionClosed">
      <div class="columns submit-bid-columns">
        <div class="column is-one-third submit-bid-form-column">
          <div class="label">Submit Bid</div>

          <div class="box overflow-scroll">

            <o-field :label="`Bid for Trade*`"
                     addons-class="addons-class"
                     :variant="error('trade_id') ? 'danger' : ''"
                     :message="error('trade_id')"
                     v-if="bidders?.length > 1"
          >
              <o-dropdown v-model="trade"
                          aria-role="list"
                          menu-class="menu-class"
                          scrollable
                          :max-height="300"
                          :required="true"
              >
                <template #trigger="{ active }">
                  <div class="button is-secondary is-outlined">
                    <span>{{ trade?.full_label || '--- Select ---' }}</span>
                    <o-icon :icon="active ? 'caret-up' : 'caret-down'" pack="fas"></o-icon>
                  </div>
                </template>

                <o-dropdown-item :value="bidder.trade" v-for="bidder in bidders">{{ bidder?.trade?.full_label }}</o-dropdown-item>
              </o-dropdown>
            </o-field>

            <o-field label="File to Upload*"
                     :variant="error('question') ? 'danger' : ''"
                     :message="error('question')"
            >
              <FileUploader
                  :key="fileUploaderKey"
                  ref="fileUploadRef"
                  class="submit-bid-file-uploader"
                  :action="storeBidFile"
                  :file-chunk-size="fileChunkSize"
                  :file-max-byte-size="uploadLimitBytes"
                  :upload-multipart-params="storeParams"
                  :multiple="true"
                  @input="handleFileInput"
                  @error="handleFileError"
              />
            </o-field>

            <div data-testid="upload-meter" class="is-size-7 mt-2 mb-4">
              <div
                  class="is-flex is-flex-wrap-wrap is-justify-content-space-between mb-1"
                  :class="{'has-text-danger': uploadExceedsLimit}"
              >
                <strong>Upload Limit:</strong>
                <span class="ml-2">{{ formatCultureNumber(selectedFileKilobytes) }} KB / {{ formatCultureNumber(uploadLimitKilobytes) }} KB</span>
              </div>
              <progress
                  class="progress is-small mb-0"
                  :class="uploadExceedsLimit ? 'is-danger' : 'is-primary'"
                  :max="uploadLimitBytes"
                  :value="Math.min(selectedFileBytes, uploadLimitBytes)"
              >
                {{ uploadPercent }}%
              </progress>
              <p v-if="uploadExceedsLimit" class="help is-danger mt-1" data-testid="upload-limit-message">
                The selected files exceed the upload limit by {{ formatCultureNumber(uploadOverageKilobytes) }} KB. Remove one or more files before submitting.
              </p>
            </div>

            <o-field label="Comment"
                     :variant="error('comment') ? 'danger' : ''"
                     :message="error('comment')"
            >
              <o-input v-model="comment" type="textarea"></o-input>
            </o-field>

            <div class="has-text-right has-margin-top-large">
              <o-button class="is-text has-margin-right" @click="handleCancel">Clear</o-button>
              <o-button class="is-primary" @click="handleSubmit" :disabled="disableSubmitButton">Submit Bid</o-button>
            </div>
          </div>
        </div>

        <div class="column is-two-thirds submitted-bids-column">
          <div class="label">Submitted Bids</div>
          <div class="box overflow-scroll">
            <CompanyFiles :bid-package-id="bidPackageId" />
          </div>
        </div>
      </div>
    </template>

  </div>
</template>

<script setup lang="ts">

import NotificationError from '@/use/NotificationError';
import NotificationSuccess from '@/use/NotificationSuccess';
import {computed, ref, toRefs, watch} from "vue";
import { FileUploader } from 'ps-fileuploaderjs';
import {storeToRefs} from "pinia";
import type {ProjectBidder} from "@/models/ProjectBidder";
import type {Trade} from "@/models/Trade";
import {useInviteStore} from "@/stores/InviteStore";
import CompanyFiles from "@/pages/Projects/CompanyFiles.vue";
import {useBidPackageCompanyFileStore} from "@/stores/BidPackageCompanyFileStore";
import {useCompanyStore} from "@/stores/CompanyStore";
import {useBidPackageProjectBidderStore} from "@/stores/BidPackageProjectBidderStore";
import {useProjectStore} from "@/stores/ProjectStore";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import {formatCultureNumber} from '@/use/formatCultureValue';

const { bidPackageProjectBidders } = storeToRefs(useBidPackageProjectBidderStore());
const bidPackageCompanyFileStore = useBidPackageCompanyFileStore();
const { company } = storeToRefs(useCompanyStore());
const { invites } = storeToRefs(useInviteStore());
const { project } = storeToRefs(useProjectStore());
const comment = ref<String>('');
const defaultTrade = ref<Trade | null>(null);
const errors = ref(<Record<any, any>>[]);
const files = ref<any[]>([]);
const fileUploadRef = ref(null);
const fileUploaderKey = ref(0);
const uploadError = ref<string | null>(null);
const trade = ref<Trade | null>(null);

const props = withDefaults(defineProps<{
  active: boolean,
  bidPackageId: number | null,
}>(), {
  active: false,
  bidPackageId: null,
});
const {
  active,
  bidPackageId,
} = toRefs(props);

const emit = defineEmits<{
  'updated': [],
}>();

const isBidPackageSubmission = computed(() => Number(bidPackageId.value) > 0);

const bidders = computed<ProjectBidder[]>(() => {
  const availableTrades = project.value?.open_invitation ? company.value?.trades || [] : [];
  const includedTradeIds = new Set<number>();

  const bidders = (bidPackageProjectBidders.value || []).filter((bidder: ProjectBidder) => {
    const tradeId = bidder.trade?.id;

    if (!tradeId || includedTradeIds.has(tradeId)) {
      return false;
    }

    includedTradeIds.add(tradeId);
    return true;
  });

  return bidders.concat(availableTrades
    .filter((availableTrade: Trade) => !includedTradeIds.has(availableTrade.id))
    .map((availableTrade: Trade) => ({
      id: availableTrade.id,
      trade: availableTrade,
    })));
});

const disableSubmitButton = computed(() => isBidSubmissionClosed.value
  || !bidUploadEnabled.value
  || uploadExceedsLimit.value
  || !files.value
  || !files.value.length
  || !project.value
  || bidPackageId.value === null
  || (bidders.value.length > 0 && !trade.value));

const fileChunkSize = computed(() => import.meta.env.VITE_APP_FILE_CHUNK_SIZE);

const legacyUploadLimitKilobytes = 250000;
const uploadLimitBytes = computed(() => {
  const configuredMaxBytes = Number(import.meta.env.VITE_APP_FILE_MAX_BYTE_SIZE);

  return Number.isFinite(configuredMaxBytes) && configuredMaxBytes > 0
    ? configuredMaxBytes
    : legacyUploadLimitKilobytes * 1024;
});
const uploadLimitKilobytes = computed(() => bytesToKilobytes(uploadLimitBytes.value));
const selectedFileBytes = computed(() => files.value.reduce(
  (total: number, file: File) => total + (Number(file.size) || 0),
  0,
));
const selectedFileKilobytes = computed(() => bytesToKilobytes(selectedFileBytes.value));
const uploadExceedsLimit = computed(() => selectedFileBytes.value > uploadLimitBytes.value);
const uploadOverageKilobytes = computed(() => bytesToKilobytes(
  Math.max(0, selectedFileBytes.value - uploadLimitBytes.value),
));
const uploadPercent = computed(() => uploadLimitBytes.value
  ? Math.min(100, Math.round((selectedFileBytes.value / uploadLimitBytes.value) * 100))
  : 0);

function bytesToKilobytes(bytes: number): number {
  return Math.round((bytes / 1024) * 100) / 100;
}

const bidUploadEnabled = computed(() => project.value?.show_bid_upload === true);
const showInvites = computed(() => Boolean(invites.value?.length));
const showUpload = computed(() => bidUploadEnabled.value && (!company.value?.client.bid_forms_hide_upload || !invites.value?.length));
const isProjectArchived = computed(() => !!project.value?.archived_at);
const isBidSubmissionClosed = computed(() => isProjectArchived.value || project.value?.accepting_bid_submissions === false);
const bidSubmissionClosedMessage = computed(() => isProjectArchived.value
  ? 'This project is archived and no longer accepts bid submissions.'
  : 'The sealed-bid due date has passed and this project no longer accepts bid submissions.');

const storeParams = computed(() => {
  return {
    params: {
      projectId: project.value?.id,
      bidPackageId: bidPackageId.value,
    },
    description: comment.value,
    trade_id: trade.value?.id ?? null,
  };
});

function clearForm() {
  trade.value = defaultTrade.value;
  files.value = [];
  comment.value = '';
  fileUploaderKey.value++;
}

function clearErrors() {
  errors.value = [];
}

function error(key: string) {
  return errors.value?.find((error: any) => error.hasOwnProperty('key') && error.key === key)?.detail || '';
}

function handleCancel() {
  clearForm();
}

function handleFileInput(uploadedFiles: File[]) {
  files.value = uploadedFiles;
}

function handleFileError(error: string) {
  const message = uploadError.value || error;
  uploadError.value = message;

  NotificationError({
    message,
  });
}

function storeBidFile(payload: any) {
  return bidPackageCompanyFileStore.store(payload).catch((error: any) => {
    uploadError.value = typeof error === 'string'
      ? error
      : error?.message || 'Server Error';
    throw error;
  });
}

function handleSubmit() {

  if (!bidUploadEnabled.value || isBidSubmissionClosed.value || uploadExceedsLimit.value || !files.value || !files.value.length || !project.value || bidPackageId.value === null || !fileUploadRef.value) {
    return;
  }

  uploadError.value = null;
  fileUploadRef.value.uploadFiles()
      .then(() => {
        // The shared uploader currently resolves after an individual upload
        // action fails, so verify this submission attempt did not capture an API error.
        if (uploadError.value) {
          return;
        }

        NotificationSuccess({
          message: 'Bid submitted successfully.',
        });
        clearForm();
      }).catch((err: any) => {
        if (!uploadError.value) {
          NotificationError({
            message: typeof err === 'string' ? err : err?.message || 'Server Error',
          });
        }
        console.error(err);
      }).finally(() => {
        emit('updated');
      });
}

watch([active, bidders], ([isActive, availableBidders]) => {
  if (!isActive) {
    return;
  }

  if (availableBidders.length === 1) {
    trade.value = availableBidders[0].trade;
  } else if (!trade.value || !availableBidders.some((bidder) => bidder.trade?.id === trade.value?.id)) {
    trade.value = null;
  }

  defaultTrade.value = trade.value;
}, {immediate: true});

</script>

<style lang="scss" scoped>

#submit-bid-tab {
  position: relative;

  .submit-bid-loading {
    align-items: center;
    background: rgba($white, .8);
    display: flex;
    inset: 0;
    justify-content: center;
    position: absolute;
    z-index: 2;
  }

  .box {
    &.overflow-scroll {
      overflow-x: auto;
    }
  }

  a {
    &.is-link {
      border: 0 !important;
    }
  }

  ul {
    border: 0 !important;
  }
}

.file-upload-picker {
  width: 100%;

  .file-upload {
    display: block;
    padding: .25em;
    border: 1px dashed #b5b5b5;
    border-radius: 6px;
    cursor: pointer;

    &.hover-blue {
      &:hover {
        border-color: #0067b8;
        background-color: rgba(0, 103, 184, 0.05);
      }
    }

    &.hover-purple {
      &:hover {
        border-color: #3E3DB2;
        background-color: rgba(62, 61, 178, 0.05);
      }
    }
  }

  #file-upload-status {
    font-style: italic;
  }
}

</style>

<style lang="scss">

#submit-bid-tab {

  // FileUploader currently renders its own legacy full-page Oruga loader while
  // its action is running. SubmitBid owns the loading experience so uploads do
  // not stack that spinner beneath the shared LoadingIndicator above.
  .submit-bid-file-uploader > .loading {
    display: none !important;
  }

  .tabs {
    a {
      &.is-link {
        border: 0;
      }
    }
  }

  .dropdown {
    a {
      &.dropdown-item {
        justify-content: left;

        &.is-active {
          color: $white !important;
        }
      }
    }
  }

}

</style>
